import Vue from 'vue';
import Vuex from 'vuex';
import moment from 'moment';

Vue.use(Vuex);
Vue.prototype.moment = moment;

// Shown to a customer whose checkout failed in a way that leaves it unknown
// whether their order was created. Matches the number in layouts/_navigation.
const SUPPORT_PHONE = '1-855-424-4224';

const store = new Vuex.Store({
  state: {
    loading: true,
    orderStepIndex: 0,
    deliveryDate: '',
    deliveryDates: [],
    customErrors: [],
    // Set when a checkout fails in a way that leaves it unknown whether the
    // order was created. Disables the Submit button so the customer cannot
    // create a duplicate while we do not know the outcome of the first attempt.
    blockResubmit: false,
    discountCodeError: '',
    customMessages: [],
    soilItems: [],
    otherItems: [],
    division: [],
    taxes: [],
    delivery_charge: 0.00,
    zone: [],
    partner: [],
    wincodes: [],
    regions: {},
    bulkProductLineItems: {},
    otherProductLineItems: {},

    stripe: {},
    paymentInfo: {
      sanitizedCcNumber: '',
      cardType: '',
      expiryDate: '',
    },

    customer: {
      firstName: '',
      lastName: '',
      email: '',
      emailConfirm: '',
      emailMe: false,
      phone: '',
      wincode: '',
      media_id: '',
      bagReturnsCount: 0,

      address: {
        street: '',
        city: '',
        region: null,
        postal: '',
        markerLocation: '',
      },
    },

    postalModal: {
      postalError: '',
      showModal: false,
    },
    adDivisionMismatch: {
      adDivisionError: false,
      showModal: false,
    },

    showFlyout: false,
    wincode: {},
    media_id: '',
    adProductId: null,
    adDivisionId: null,
    addedToCart: false,
    railsEnv: '',
  },

  getters: {
    bulkItemArray: (state) => {
      const bulkItemIds = Object.keys(state.bulkProductLineItems);
      const bulkItems = bulkItemIds.map((itemId) => state.bulkProductLineItems[itemId]);
      return bulkItems;
    },

    otherItemArray: (state) => {
      const otherProductIds = Object.keys(state.otherProductLineItems);
      const otherItems = otherProductIds.map((itemId) => (
        state.otherProductLineItems[itemId]));
      return otherItems;
    },

    subTotal: (state, getters) => {
      const bulkItemPrice = getters.bulkItemArray.reduce(
        (total, item) => total + (item.base_price * item.quantity), 0,
      );
      const otherItemPrice = getters.otherItemArray.reduce(
        (total, item) => total + (item.base_price * item.quantity), 0,
      );

      return (bulkItemPrice + otherItemPrice);
    },

    taxableSubtotal: (state, getters) => {
      const bulkItemPrice = getters.bulkItemArray.filter((item) => item.taxable).reduce(
        (total, item) => total + (item.base_price * item.quantity), 0,
      );
      const otherItemPrice = getters.otherItemArray.filter((item) => item.taxable).reduce(
        (total, item) => total + (item.base_price * item.quantity), 0,
      );
      const wincodeDiscount = state.wincode.discount || 0;
      
      // Bag discounts are applied wholesale to the enture order regardless of where they came from.
      // This is a problem if bulk items are a mixture of taxable and not since discount will be applied to the taxable item.
      return (bulkItemPrice + otherItemPrice + state.delivery_charge - getters.bagDiscounts - wincodeDiscount);
    },

    // Subtotal of items, delivery and bag discounts.
    // Wincode and taxes are not included.
    discountableSubtotal: (state, getters) => {
      const bulkItemPrice = getters.bulkItemArray.reduce(
        (total, item) => total + (item.base_price * item.quantity), 0,
      );
      const otherItemPrice = getters.otherItemArray.reduce(
        (total, item) => total + (item.base_price * item.quantity), 0,
      );

      return (bulkItemPrice + otherItemPrice + state.delivery_charge - getters.bagDiscounts);
    },

    orderTaxes: (state, getters) => {
      const orderTaxes = state.taxes.map((tax) => {
        const calculatedTaxes = { ...tax, total: Math.max(0, (getters.taxableSubtotal * tax.rate)) };
        return calculatedTaxes;
      });
      return orderTaxes;
    },

    bagDiscounts: (state, getters) => {
      const bagDiscounts = getters.bulkItemArray.reduce((total, item, index) => {
        let discount = 0;
        if (index === 0) {
          discount = parseFloat(item.discount_first, 10) + ((item.quantity - 1) * item.discount_additional);
        } else {
          discount = parseFloat(item.quantity * item.discount_additional);
        }
        return (total + discount);
      }, 0);
      return bagDiscounts;
    },

    totalQuantity: (state, getters) => {
      const bulkQuantity = getters.bulkItemArray.reduce(
        (total, item) => parseInt(total, 10) + parseInt(item.quantity, 10), 0,
      );
      const otherQuantity = getters.otherItemArray.reduce(
        (total, item) => parseInt(total, 10) + parseInt(item.quantity, 10), 0,
      );

      return parseInt(bulkQuantity, 10) + parseInt(otherQuantity, 10);
    },

    orderTotal: (state, getters) => {
      const totalTaxes = getters.orderTaxes.reduce((total, item) => (total + item.total), 0);
      const total = getters.subTotal - getters.bagDiscounts - state.wincode.discount
      + state.delivery_charge + totalTaxes;
      
      return total;
    },

    pricingAvailabilityChanged: (state, getters) => {
      let changed = false;
      // Check each product in cart for pricing/availability change
      const checkCartItems = (type, getter) => {
        getters[getter].forEach((cartItem) => {
          if (!changed) {
            const updatedItem = state[type].find((item) => item.id === cartItem.id);

            if (updatedItem) {
              // Pricing changed
              changed = (
                cartItem.base_price !== updatedItem.base_price
                || cartItem.discount_first !== updatedItem.discount_first
                || cartItem.discount_additional !== updatedItem.discount_additional
                || cartItem.discount_first_jr !== updatedItem.discount_first_jr
                || cartItem.discount_additional_jr !== updatedItem.discount_additional_jr
              );
            } else { // Item removed
              changed = true;
            }
          }
        });
      };

      checkCartItems('soilItems', 'bulkItemArray');
      checkCartItems('otherItems', 'otherItemArray');

      return changed;
    },
  },

  mutations: {
    setProperties(state, data) {
      return Object.keys(data).map((key) => {
        if (Object.prototype.hasOwnProperty.call(state, key)) {

          state[key] = data[key];
        }

        return state[key];
      });
    },

    setPostalCode(state, data) {
      state.customer.address.postal = data;
    },

    addToPropertyArrays(state, data) {
      return Object.keys(data).map((key) => {
        if (Object.prototype.hasOwnProperty.call(state, key)) {
          const newArray = [...state[key], ...data[key]];
          state[key] = newArray;
        }
        return state[key];
      });
    },

    addLineItem(state, data) {
      const items = { ...state[data.itemType] };
      items[data.item.id] = data.item;

      state[data.itemType] = items;
    },

    removeLineItem(state, data) {
      const bulkItems = { ...state[data.itemType] };
      delete bulkItems[data.item.id];
      state[data.itemType] = bulkItems;
    },
  },
  actions: {

    setNewPostal({ state, commit, dispatch }, postalCode) {
      const csrfToken = document.querySelector('[name="csrf-token"]').content;
      commit('setProperties', { loading: true });

      fetch('/orders/restart_order', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ postal: postalCode }),
      }).then((response) => {
        if (response.ok) {
          return response.json();
        }
        throw response;
      }).then((data) => {
        dispatch('prepEmptyOrder', data.postal);
        dispatch('fetchPricing');
        dispatch('addAdProduct');
        dispatch('setStripe', data.stripe_pub_key);
        commit('setProperties', {
          orderStepIndex: 0,
          postalModal: {
            showModal: false,
            postalError: '',
          },
        });
      }).catch(async (error) => {
        // loading was only ever cleared inside the parsed-JSON path, so a
        // non-JSON error response left the app stuck on the loading screen
        // forever. Always clear it, and always show something.
        let outOfArea = false;
        let message = 'We could not check that postal code just now. Please try again in a moment.';

        try {
          if (error && typeof error.json === 'function') {
            const newError = await error.json();
            outOfArea = !!(newError && newError.out_of_area);
            if (newError && newError.message) { message = newError.message; }
          }
        } catch (parseError) {
          // Body was not JSON. Keep the generic message.
        }

        if (outOfArea) {
          window.location.href = '/out_of_area';
          return;
        }

        const { showModal } = state.postalModal;
        commit('setProperties', { postalModal: { showModal, postalError: message } });
        commit('setProperties', { loading: false });
      });
    },

    addLineItem({ state, commit, dispatch }, data) {
      // Commit mutation of line items
      commit('addLineItem', data);
      // Update LocalStorage cart
      const items = { ...state[data.itemType] };
      localStorage.setItem(data.itemType, JSON.stringify(items));
      // Update Navigation count
      dispatch('updateNavQty');
      // Track Reddit Add to Cart if not already tracked
      if (!state.addedToCart) {
        dispatch('setAddedToCart', data.itemType);
      }
    },

    removeLineItem({ state, commit, dispatch }, data) {
      // Commit mutation of line items
      commit('removeLineItem', data);
      // Update LocalStorage cart
      const items = { ...state[data.itemType] };
      localStorage.setItem(data.itemType, JSON.stringify(items));
      // Update Navigation count
      dispatch('updateNavQty');
    },

    /**
     * Loads cart from LocalStorage across sessions
     */
    loadLocalCart({ state, commit, dispatch }) {
      if (localStorage.getItem('bulkProductLineItems')) {
        commit('setProperties', {
          bulkProductLineItems: JSON.parse(localStorage.getItem('bulkProductLineItems')),
        });
      }

      if (localStorage.getItem('otherProductLineItems')) {
        commit('setProperties', {
          otherProductLineItems: JSON.parse(localStorage.getItem('otherProductLineItems')),
        });
      }

      if (state.adProductId) {
        dispatch('addAdProduct');
      }
    },

    addAdProduct({ state, commit, dispatch, getters }) {
      // Ensure that pricing is loaded, retry if not
      if (state.soilItems.length === 0) {
        setTimeout(() => {
          dispatch('addAdProduct');
        }, 100);
        return;
      }

      if (state.adProductId) {
        // Look to see if this product is in the cart already, if so, skip
        const cartProduct = getters.bulkItemArray.find((product) => product.id === state.adProductId);
        if (!cartProduct) {
          // Add the quantity to the product found and format the data for adding
          const soilProduct = state.soilItems.find((product) => product.id === state.adProductId);
          
          // Check if the product is available in the current division
          // If not, show the adDivisionMismatch modal with an error
          if (!soilProduct) {
            commit('setProperties', { adDivisionMismatch: { showModal: true, adDivisionError: true } });
            return;
          }

          const currentItem = {
            ...soilProduct,
            quantity: 1,
          };
          const data = {
            itemType: 'bulkProductLineItems',
            item: currentItem,
          };

          // Add the product to the cart
          dispatch('addLineItem', data);
          // Show flyout since a new product was added
          commit('setProperties', { showFlyout: true });
        }
        
        // Check if the adDivisionId is different than the current division
        // If so, show the adDivisionMismatch modal with a warning
        if (state.adDivisionId !== state.division.id) {
          commit('setProperties', { adDivisionMismatch: { showModal: true, adDivisionError: false } });
        }
        // Remove from state since its been applied to the cart
        commit('setProperties', { adProductId: null });
      }
    },

    /**
     * Updates quantity in Navigation menu cart icon
     */
    updateNavQty({ state, dispatch }) {
      const countEl = document.getElementById('nav-cart-count');
      countEl.innerText = this.getters.totalQuantity;
      // NOTE: If wincode exists, check discount again since cart has changed.
      // This gets triggered from any lineItem Add and Removal!
      const discountCode = state.wincode.code || '';
      if (discountCode.length > 0) {
        dispatch('getDiscount', state.wincode.code);
      }
    },

    setAddedToCart({ dispatch, commit }, itemType) {
      if (itemType === 'bulkProductLineItems') {
        commit('setProperties', { addedToCart: true });
        dispatch('trackRedditAddToCart');
      }
    },

    ecommerceTrack({ state, getters }, orderId) {

      const bulkItems = getters.bulkItemArray.map((item) => {
        const bulk = {
          'name': item.name,
          'id': `OTHER_${item.id}`,
          'price': item.base_price,
          'quantity': item.quantity,
          'coupon': '',
        }
        return bulk;
      });

      const otherItems = getters.otherItemArray.map((item) => {
        const other = {
          'name': item.name,
          'id': `OTHER_${item.id}`,
          'price': item.base_price,
          'quantity': item.quantity,
          'coupon': '',
        }
        return other;
      });
      // this combines the arrays, by pushing otherItems into bulkItems
      Array.prototype.push.apply(bulkItems,otherItems);

      const taxTotal = getters.orderTaxes.reduce((prev, tax) => (tax.total + prev), 0);
      const dataLayer = window.dataLayer || [];
      dataLayer.push({
        'event':'transaction',
        'ecommerce': {
          'purchase': {
            'actionField': {
              'id': orderId,
              'revenue': getters.taxableSubtotal,
              'tax': taxTotal,
            },
            'products': bulkItems
          }
        }
      });
    },

    triggerG4purchaseEvent({state, getters}, order) {
      if (state.railsEnv == 'production') {
        const taxTotal = getters.orderTaxes.reduce((prev, tax) => (tax.total + prev), 0);
        const itemArray = [];
        // loop through bulk product line items and push into array
        order.bulk_product_line_items.items.forEach(function(itm) {
          itemArray.push({
            item_name: itm.name,
            quantity: itm.quantity,
            item_category: "Bulk Product"
          });
        });

        // loop through other product line items and push into array
        order.other_product_line_items.items.forEach( function(itm) {
          itemArray.push({
            item_name: itm.name,
            quantity: itm.quantity,
            item_category: "Other Product"
          });
        });

        gtag("event", "purchase", {
          transaction_id: order.order_number,
          value: order.total,
          tax: taxTotal,
          shipping: state.delivery_charge,
          currency: state.customer.address.postal.length === 6 ? 'CAD' : 'USD',
          items: itemArray,
        });
      }
    },

    conversionTrack({ state, getters }) {
      // Fb conversion tracking
      const orderDetails = {
        value: getters.taxableSubtotal,
        currency: state.customer.address.postal.length === 6 ? 'CAD' : 'USD',
      };

      if (typeof window.fbq !== 'undefined') {
        window.fbq('track', 'Purchase', orderDetails);
      }
    },

    trackRedditPageView({ state }) {
      if (typeof window.rdt !== 'undefined') {
        window.rdt('track', 'PageVisit');
      }
    },

    trackSpotifyPageView({ state }) {
      if (typeof window.spdt !== 'undefined') {
        window.spdt('view');
      }
    },

    trackSpotifyAlias({ state }) {
      if (typeof window.spdt !== 'undefined') {
        window.spdt('alias', {
          'email': state.customer.email,
        });
      }
    },

    trackRedditAddToCart({ state }) {
      if (typeof window.rdt !== 'undefined') {
        window.rdt('track', 'AddToCart');
      }
    },

    trackRedditPurchase({ state }) {
      if (typeof window.rdt !== 'undefined') {
        window.rdt('track', 'Purchase');
      }
    },

    trackSpotifyPurchase({ state }) {
      if (typeof window.spdt !== 'undefined') {
        window.spdt('purchase');
      }
    },

    setStripe({ commit }, stripePubKey) {
      const stripe = window.Stripe(stripePubKey);
      commit('setProperties', { stripe });
    },

    fetchPricing({ commit }) {
      return fetch('/pricing')
        .then((response) => response.json())
        .then((data) => {
          commit('setProperties', { division: data['division'] });
          commit('setProperties', { taxes: data['taxes'] });
          commit('setProperties', { delivery_charge: data['delivery_charge'] });
          commit('setProperties', { zone: data['zone'] });
          commit('setProperties', { partner: data['partner'] });
          commit('setProperties', { wincodes: data['wincodes'] });
          commit('setProperties', { deliveryDates: data['delivery_dates'] });
          commit('setProperties', { soilItems: data['soil_items'] });
          commit('setProperties', { otherItems: data['other_items'] });
          commit('setProperties', { loading: false });
          commit('setProperties', { regions: data['regions'] });
        })
        .catch((error) => {
          commit('setProperties', { loading: false });
          if (error instanceof Error) {
            return { error };
          }
          return {
            error: new Error(
              `HTTP ${error.status} ${error.statusText}`,
            ),
          };
        });
    },

    updateCartPricingAvailability({ state, getters, dispatch }) {
      // Check each product in cart for pricing/availability change
      const updateCartItems = (type, getter, cat) => {
        getters[getter].forEach((cartItem) => {
          const updatedItem = state[type].find((item) => item.id === cartItem.id);

          if (updatedItem) {
            // Price changed
            if (
              cartItem.base_price !== updatedItem.base_price
              || cartItem.discount_first !== updatedItem.discount_first
              || cartItem.discount_additional !== updatedItem.discount_additional
              || cartItem.discount_first_jr !== updatedItem.discount_first_jr
              || cartItem.discount_additional_jr !== updatedItem.discount_additional_jr
            ) {
              // Update cart item, retaining quantity
              updatedItem.quantity = cartItem.quantity;
              dispatch('removeLineItem', { item: cartItem, itemType: cat });
              dispatch('addLineItem', { item: updatedItem, itemType: cat });
            }
          } else { // Item no longer available
            dispatch('removeLineItem', { item: cartItem, itemType: cat });
          }
        });
      };

      updateCartItems('soilItems', 'bulkItemArray', 'bulkProductLineItems');
      updateCartItems('otherItems', 'otherItemArray', 'otherProductLineItems');
    },

    submitOrder({ state, commit, dispatch }, card_token) {

      commit('setProperties', { loading: true, customErrors: [] });
      // Fetch JSON security token from meta tag
      const csrfToken = document.querySelector('[name="csrf-token"]').content;

      const order = {
        id: null,
        zone_id: state.zone.id,
        zone: {
          id: state.zone.id,
          name: state.zone.name,
          active: state.zone.active,
        },
        taxes: '',
        bag_returns: state.customer.bagReturnsCount || 0,
        delivery_date: state.deliveryDate,
        delivery_time: 'Any time',
        delivery_time_summary: 'Any',
        delivery_comments: state.customer.address.markerLocation,
        order_date: moment().format('YYYY-MM-DD'),
        address: {
          street: state.customer.address.street,
          city: state.customer.address.city,
          postal_code: state.customer.address.postal,
          region: state.customer.address.region,
        },
        bulk_product_line_items: {
          first_bag_discount: 0,
          additional_bags_discount: 0,
          custom_discount: 0,
          zone_charge: 0,
          items: state.bulkProductLineItems,
        },
        other_product_line_items: {
          custom_discount: 0,
          items: state.otherProductLineItems,
        },
        pickup: false,
        skids_in: 0,
        skids_out: 0,
        status: 'Open',
        subtotal_amount: 0,
        total: 0,
        wincode: state.wincode.code,
        wincode_total: state.wincode.discount || 0,
        media_id: state.media_id,
        contact: {
          email: state.customer.email,
          first_name: state.customer.firstName,
          last_name: state.customer.lastName,
          postal_code: state.customer.address.postal,
          partner_id: state.partner.id,
          email_me: state.customer.emailMe,
        },
        customer: {
          id: null,
          active: true,
          comments: '',
          contacts: [],
          do_not_mail: true,
          email: state.customer.email,
          first_name: state.customer.firstName,
          is_person: true,
          last_name: state.customer.lastName,
          phone: state.customer.phone,
          subscribe_to_mailchimp: false,
          tax_exemption: false,
          address: {
            street: state.customer.address.street,
            city: state.customer.address.city,
            region: state.customer.address.region,
            postal_code: state.customer.address.postal,
            latitude: null,
            longitude: null,
            directions: '',
          },
          division: {
            id: null,
            name: '',
          },
        },
        payment: {
          amount: 0,
          capture: '',
          type: '',
          card_token,
        },
      };

      // Returned so callers can await the whole submission. paymentInfo.vue
      // awaits it before clearing its own loading flag.
      return fetch('/api_v2/agriorders', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(order),
      }).then((response) => {
        if (response.ok) {
          return response.json();
        }
        throw response;
      }).then((data) => {
        dispatch('ecommerceTrack', data.id);
        dispatch('conversionTrack');
        dispatch('triggerG4purchaseEvent', data);
        // Clear local cart on success
        localStorage.removeItem('bulkProductLineItems');
        localStorage.removeItem('otherProductLineItems');
        // Update Navigation count
        dispatch('updateNavQty');
        // Go to receipt page
        commit('setProperties', { orderStepIndex: 5 });
        commit('setProperties', { loading: false });
      }).catch(async (error) => {
        // Anything that is not an explicitly retryable server response must be
        // treated as "outcome unknown". An unhandled 500 serves public/500.html,
        // and a dropped connection rejects with a TypeError that has no .json at
        // all, so neither can be parsed. Previously the parse failure was
        // unhandled, customErrors was never populated, and the customer was
        // returned to a blank payment form with no message of any kind. That is
        // what led customers to submit a second time.
        // Kept separate so a server message that already explains the problem is
        // not followed by a second sentence repeating it.
        const DO_NOT_RESUBMIT = 'Please do not submit again \u2014 call us at '
          + `${SUPPORT_PHONE} and we will confirm it for you.`;
        const UNKNOWN_OUTCOME = 'We could not confirm whether your order went through, and we '
          + 'do not want to risk charging you twice.';

        let message = `${UNKNOWN_OUTCOME} ${DO_NOT_RESUBMIT}`;
        let retryable = false;

        try {
          if (error && typeof error.json === 'function') {
            const parsed = await error.json();
            const raw = (parsed && typeof parsed.message === 'object' && parsed.message !== null)
              ? parsed.message.message
              : (parsed || {}).message;
            if (parsed && parsed.retryable === true && raw) {
              message = raw;
              retryable = true;
            } else if (raw) {
              message = `${raw} ${DO_NOT_RESUBMIT}`;
            }
          }
        } catch (parseError) {
          // Body was not JSON: an unhandled 500 serves public/500.html. Keep the
          // unknown-outcome message, which is the correct reading of that state.
        }

        commit('addToPropertyArrays', { customErrors: [message] });
        if (!retryable) {
          commit('setProperties', { blockResubmit: true });
        }
        // Commit the message before clearing loading, so it is already in the
        // store when index.vue swaps the loading screen back for the form.
        commit('setProperties', { loading: false });

        if (typeof window !== 'undefined' && window.scrollTo) {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      });
    },

    getDiscount({ state, commit, dispatch, getters }, code) {
      // Fetch JSON security token from meta tag
      const csrfToken = document.querySelector('[name="csrf-token"]').content;

      const firstDiscount = parseFloat(getters.bulkItemArray[0].discount_first * -1, 10);
      const additionalDiscount = parseFloat(getters.bagDiscounts * -1, 10) - firstDiscount;

      const order = {
        delivery_date: state.deliveryDate,
        order_date: moment().format('YYYY-MM-DD'),
        code: code,
        division_id: state.division.id,
        discount: 0,
        bulk_product_line_items: {
          first_bag_discount: firstDiscount,
          additional_bags_discount: additionalDiscount,
          custom_discount: 0,
          items: getters.bulkItemArray,
        },
        other_product_line_items: {
          custom_discount: 0,
          items: getters.otherItemArray,
        }
      };
      // Remove old wincode and media_id since a new on will be created
      commit('setProperties', {
        media_id: '',
        wincode: '',
        discountCodeError: '',
      });

      fetch('/api_v2/verify_discount', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(order),
      }).then((response) => {
        if (response.ok) {
          return response.json();
        }
        throw response;
      }).then((data) => {
        if (data['errors'] !== undefined && data['errors'].length > 0) {
          console.log("newError: ", data['errors'][0]['message']);
          commit('setProperties', {discountCodeError: data['errors'][0]['message']});
          state.customer.wincode = '';
          
          // incase valid discount code applied first and remove it later
          commit('setProperties', {
            media_id: '',
            wincode: '',
          });
        } else if (data['discount'] === 0 && data['discount_type'] !== 'WinCode') {
          const unappliedDiscountMessage = "This is a valid code for " + data['discount_code_name'] + ", but no discount has been applied." +
          " You may need to modify the contents of your order to qualify for this discount."
          commit('setProperties', { discountCodeError: unappliedDiscountMessage });
          state.customer.wincode = '';
          
          // incase valid discount code applied first and remove it later
          commit('setProperties', {
            media_id: '',
            wincode: '',
          });
        } else {
          if (data['order_number'] != null) {
            commit('setProperties', {discountCodeError: "Single-use Code "+ data['discount_code'] +" has already been claimed (Order #"+ data['order_number'] +")"});
          } else {
            // clear error message
            commit('setProperties', {discountCodeError: ''});

            if (data['media_id'] !== null && data['media_id'] !== undefined) {
              commit('setProperties', {media_id: data['media_id']});
            }

            let maxDiscount = data['discount'];
            if (data['discount'] >= getters.discountableSubtotal) {
              maxDiscount = 0;
              commit('setProperties', {
                discountCodeError: `Discount ($${data['discount']}) exceeds cart subtotal, please add more to apply.`
              });
            }

            commit('setProperties', {
              wincode: {code: data['discount_code'], discount: maxDiscount, name: data['discount_code_name'], discount_type: data['discount_type']}
            });
          }
        }
      })
    },

    prepEmptyOrder({ commit }, postalCode) {
      commit('setProperties', {
        deliveryDate: '',
        customErrors: [],
        customMessages: [],
        taxes: [],
        delivery_charge: 0.00,
        bulkProductLineItems: {},
        otherProductLineItems: {},

        customer: {
          firstName: '',
          lastName: '',
          email: '',
          emailConfirm: '',
          emailMe: false,
          phone: '',
          wincode: '',
          bagReturnsCount: 0,

          address: {
            street: '',
            city: '',
            region: null,
            postal: postalCode,
            markerLocation: '',
          },
        },

        wincode: {},
      });
    },
  },
});

export default store;
